import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community/dist/lib/main';
import { PulseLoader } from 'react-spinners';
import colorSet from '@/src/styles/color';
import { cn, createEmptyObject } from '@/src/utils';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import { UseFormReturn } from 'react-hook-form';
import { ICellRendererParams } from 'ag-grid-community';
import { BUTTON_CONST } from '@/src/constants/buttons';

export interface TableProps {
  form: UseFormReturn<any>;
  tableField: string;
  columnDefs: ColDef[];
  rowData?: GridOptions['rowData'];
  isLoading?: boolean;
  containerClassName?: string;
  tableClassName?: string;
  onGridReady?: (params: GridOptions) => void;
  onDeleteRow?: (deletedId: number) => void;
}

const FormTable = forwardRef<AgGridReact, TableProps>(
  (
    {
      form,
      tableField,
      rowData,
      columnDefs,
      containerClassName,
      tableClassName,
      onDeleteRow,
      ...props
    },
    ref,
  ) => {
    const gridRef = useRef<AgGridReact>();
    const [rowDataState, setRowDataState] = useState<GridOptions['rowData']>(
      [],
    );

    useEffect(() => {
      setRowDataState(rowData ?? []);
    }, [rowData]);

    useEffect(() => {
      window.addEventListener('error', e => {
        if (
          e.message === 'ResizeObserver loop limit exceeded' ||
          e.message ===
            'ResizeObserver loop completed with undelivered notifications.'
        ) {
          const resizeObserverErrDiv = document.getElementById(
            'webpack-dev-server-client-overlay-div',
          );
          const resizeObserverErr = document.getElementById(
            'webpack-dev-server-client-overlay',
          );
          if (resizeObserverErr) {
            resizeObserverErr.setAttribute('style', 'display: none');
          }
          if (resizeObserverErrDiv) {
            resizeObserverErrDiv.setAttribute('style', 'display: none');
          }
        }
      });
    }, []);

    const defaultColDef = useMemo(
      () => ({
        resizable: true,
        sortable: false,
        comparator: () => 0,
      }),
      [],
    );

    const handleNewRow = useCallback(() => {
      const defaultObj = createEmptyObject(rowDataState?.[0]);
      const addedRow = rowDataState
        ? [...rowDataState, defaultObj]
        : [defaultObj];
      setRowDataState(addedRow);
      form.setValue(tableField, addedRow);
    }, [form, tableField, rowDataState]);

    const handleDeleteRow = useCallback(
      (rowIndex: number) => {
        const deletedRow = rowDataState?.filter(
          (_, index) => index !== rowIndex,
        );
        setRowDataState(deletedRow);
        form.setValue(tableField, deletedRow);
        onDeleteRow?.(rowDataState?.[rowIndex]?.id);
      },
      [form, tableField, rowDataState, onDeleteRow],
    );

    const renderLoadingComponent = () => {
      return <PulseLoader color={colorSet.blue4} />;
    };

    return (
      <div
        className={cn(
          'ag-theme-alpine ag-theme-form border border-gray0',
          containerClassName,
        )}
      >
        <AgGridReact
          className={tableClassName}
          ref={node => {
            if (node) {
              gridRef.current = node;
            }
            if (ref) {
              if (typeof ref === 'function') {
                ref(node);
              } else {
                ref.current = node;
              }
            }
          }}
          enableCellTextSelection
          rowData={rowDataState}
          onRowClicked={() => {}}
          onRowSelected={() => {}}
          domLayout="autoHeight"
          columnDefs={[
            ...columnDefs,
            {
              headerName: BUTTON_CONST.delete,
              headerClass: 'ag-right-aligned-cell',
              cellStyle: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              },
              width: 96,
              minWidth: 96,
              pinned: 'right',
              lockPosition: 'right',
              cellRenderer: ({ rowIndex }: ICellRendererParams) => {
                return (
                  <Button
                    className="flex h-[24px] w-[57px] gap-[4px] p-0 !text-b12r !leading-[0]"
                    buttonColor="red"
                    name={BUTTON_CONST.delete}
                    onClick={() => handleDeleteRow(rowIndex)}
                  >
                    <LucideIcon name="X" size={14} color="red5" />
                    <span>{BUTTON_CONST.delete}</span>
                  </Button>
                );
              },
            },
          ]}
          defaultColDef={defaultColDef}
          loadingOverlayComponent={() => renderLoadingComponent()}
          {...props}
        />
        <Button
          buttonColor="outline"
          className="m-[8px] box-border flex w-[calc(100%-16px)] justify-center gap-[4px]"
          onClick={handleNewRow}
        >
          <LucideIcon name="Plus" size={14} />
          품목 옵션 추가하기
        </Button>
      </div>
    );
  },
);

export default React.memo(FormTable);
